import React, { useState } from 'react';
import './Citations.css';

const SourceIcon = ({ type }) => (
  type === 'tenant' ? (
    <svg width="12" height="12" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  ) : (
    <svg width="12" height="12" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  )
);

const getSourceLabel = (answerSource, hasTenantDocs) => {
  if (answerSource === 'tenant_docs') return 'Based on your implementation docs';
  if (answerSource === 'oracle_kb') return 'Based on Oracle documentation';
  if (answerSource === 'hybrid') return 'Based on Oracle docs + AI knowledge';
  if (answerSource === 'ai_knowledge') return 'Based on AI knowledge';
  if (hasTenantDocs) return 'Based on your implementation docs';
  return 'Based on Oracle HCM knowledge base';
};

const Citations = ({ citations, hasTenantDocs, answerSource }) => {
  const [expanded, setExpanded] = useState(false);

  if (!citations || citations.length === 0) return null;

  const tenantCitations = citations.filter(
    c => c.source_type === 'tenant'
  );
  const oracleCitations = citations.filter(
    c => c.source_type === 'oracle'
  );

  const getFilename = (filename) => {
    if (!filename) return 'Unknown source';
    return filename.length > 35
      ? filename.substring(0, 35) + '...'
      : filename;
  };

  const getScoreLabel = (score) => {
    if (score >= 0.85) return 'High relevance';
    if (score >= 0.65) return 'Good match';
    return 'Related';
  };

  return (
    <div className="citations-container">
      <button
        type="button"
        className="citations-toggle"
        onClick={() => setExpanded(!expanded)}>
        <span className="citations-toggle-icon">
          {expanded ? '▾' : '▸'}
        </span>
        <span className="citations-label">
          {getSourceLabel(answerSource, hasTenantDocs)}
        </span>
        <span className="citations-count">
          {citations.length} source{citations.length !== 1 ? 's' : ''}
        </span>
      </button>

      <div className={`citations-list ${expanded ? 'expanded' : 'collapsed'}`}>
          {tenantCitations.length > 0 && (
            <div className="citations-group">
              <div className="citations-group-label">
                Your implementation docs
              </div>
              {tenantCitations.map((c, i) => (
                <div key={i} className="citation-item citation-tenant">
                  <div className="citation-icon">
                    <SourceIcon type="tenant" />
                  </div>
                  <div className="citation-info">
                    <div className="citation-filename">
                      {getFilename(c.filename)}
                    </div>
                    <div className="citation-meta">
                      {getScoreLabel(c.score)}
                      {c.is_grounded && (
                        <span className="citation-grounded"> ✓ Verified</span>
                      )}
                    </div>
                  </div>
                  <div className="citation-score">
                    {Math.round(c.score * 100)}%
                  </div>
                </div>
              ))}
            </div>
          )}

          {oracleCitations.length > 0 && (
            <div className="citations-group">
              <div className="citations-group-label">
                Oracle HCM documentation
              </div>
              {oracleCitations.map((c, i) => (
                <div key={i} className="citation-item citation-oracle">
                  <div className="citation-icon">
                    <SourceIcon type="oracle" />
                  </div>
                  <div className="citation-info">
                    <div className="citation-filename">
                      {getFilename(c.filename)}
                    </div>
                    <div className="citation-meta">
                      {c.release
                        ? `Oracle HCM ${c.release}`
                        : 'Oracle HCM documentation'}
                      {' · '}{getScoreLabel(c.score)}
                      {c.is_grounded && (
                        <span className="citation-grounded"> ✓ Verified</span>
                      )}
                    </div>
                  </div>
                  <div className="citation-score">
                    {Math.round(c.score * 100)}%
                  </div>
                </div>
              ))}
            </div>
          )}

          {!hasTenantDocs && answerSource !== 'tenant_docs' && (
            <div className="citations-no-tenant">
              <span>💡</span>
              Upload your implementation docs for
              answers specific to your Oracle setup
            </div>
          )}
      </div>
    </div>
  );
};

export default Citations;
